/**
 * InputManager - Handles keyboard and mouse input
 * Tracks key states (down, pressed, released) and mouse position/buttons
 */

import type { Vector2, InputAction } from '../types/game';
import type { KeyBindings } from '../state/types';

export interface KeyState {
  down: boolean; // Currently held down
  pressed: boolean; // Just pressed this frame
  released: boolean; // Just released this frame
  timestamp: number; // When the state last changed
}

export interface MouseState {
  position: Vector2;
  delta: Vector2;
  buttons: boolean[]; // Left, Middle, Right
  wheel: number;
}

export class InputManager {
  // Keyboard state
  private keys: Map<string, KeyState> = new Map();
  private keysToUpdate: Set<string> = new Set();

  // Mouse state
  private mousePosition: Vector2 = { x: 0, y: 0 };
  private lastMousePosition: Vector2 = { x: 0, y: 0 };
  private mouseDelta: Vector2 = { x: 0, y: 0 };
  private mouseButtons: boolean[] = [false, false, false]; // Left, Middle, Right
  private mouseWheel: number = 0;

  // Control mapping
  private keyBindings: KeyBindings | null = null;

  // Enabled state
  private enabled: boolean = true;

  constructor() {
    this.setupEventListeners();
  }

  /**
   * Set up DOM event listeners
   */
  private setupEventListeners(): void {
    // Keyboard events
    window.addEventListener('keydown', this.handleKeyDown.bind(this));
    window.addEventListener('keyup', this.handleKeyUp.bind(this));

    // Mouse events
    window.addEventListener('mousemove', this.handleMouseMove.bind(this));
    window.addEventListener('mousedown', this.handleMouseDown.bind(this));
    window.addEventListener('mouseup', this.handleMouseUp.bind(this));
    window.addEventListener('wheel', this.handleMouseWheel.bind(this));

    // Prevent context menu
    window.addEventListener('contextmenu', (e) => e.preventDefault());

    // Handle window blur (release all keys)
    window.addEventListener('blur', this.handleWindowBlur.bind(this));
  }

  /**
   * Update input state - call this once per frame BEFORE game update
   */
  public update(): void {
    if (!this.enabled) {
      return;
    }

    // Reset pressed/released states for all keys that were updated last frame
    for (const key of this.keysToUpdate) {
      const keyState = this.keys.get(key);
      if (keyState) {
        keyState.pressed = false;
        keyState.released = false;
      }
    }
    this.keysToUpdate.clear();

    // Reset mouse delta and wheel
    this.mouseDelta = {
      x: this.mousePosition.x - this.lastMousePosition.x,
      y: this.mousePosition.y - this.lastMousePosition.y,
    };
    this.lastMousePosition = { ...this.mousePosition };
    this.mouseWheel = 0;
  }

  /**
   * Handle key down event
   */
  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }

    const key = event.code;

    // Prevent key repeat from triggering "pressed"
    if (event.repeat) {
      return;
    }

    const existingState = this.keys.get(key);
    if (!existingState || !existingState.down) {
      this.keys.set(key, {
        down: true,
        pressed: true,
        released: false,
        timestamp: Date.now(),
      });
      this.keysToUpdate.add(key);
    }
  }

  /**
   * Handle key up event
   */
  private handleKeyUp(event: KeyboardEvent): void {
    if (!this.enabled) {
      return;
    }

    const key = event.code;
    const existingState = this.keys.get(key);

    if (existingState) {
      this.keys.set(key, {
        down: false,
        pressed: false,
        released: true,
        timestamp: Date.now(),
      });
      this.keysToUpdate.add(key);
    }
  }

  /**
   * Handle mouse move event
   */
  private handleMouseMove(event: MouseEvent): void {
    if (!this.enabled) {
      return;
    }

    this.mousePosition = {
      x: event.clientX,
      y: event.clientY,
    };
  }

  /**
   * Handle mouse down event
   */
  private handleMouseDown(event: MouseEvent): void {
    if (!this.enabled) {
      return;
    }

    this.mouseButtons[event.button] = true;
  }

  /**
   * Handle mouse up event
   */
  private handleMouseUp(event: MouseEvent): void {
    if (!this.enabled) {
      return;
    }

    this.mouseButtons[event.button] = false;
  }

  /**
   * Handle mouse wheel event
   */
  private handleMouseWheel(event: WheelEvent): void {
    if (!this.enabled) {
      return;
    }

    this.mouseWheel = event.deltaY;
  }

  /**
   * Handle window blur - release all keys
   */
  private handleWindowBlur(): void {
    // Release all keys
    for (const [key, state] of this.keys) {
      if (state.down) {
        this.keys.set(key, {
          down: false,
          pressed: false,
          released: true,
          timestamp: Date.now(),
        });
        this.keysToUpdate.add(key);
      }
    }

    // Release all mouse buttons
    this.mouseButtons = [false, false, false];
  }

  // === Keyboard Query Methods ===

  /**
   * Check if a key is currently held down
   */
  public isKeyDown(key: string): boolean {
    const state = this.keys.get(key);
    return state ? state.down : false;
  }

  /**
   * Check if a key was just pressed this frame
   */
  public isKeyPressed(key: string): boolean {
    const state = this.keys.get(key);
    return state ? state.pressed : false;
  }

  /**
   * Check if a key was just released this frame
   */
  public isKeyReleased(key: string): boolean {
    const state = this.keys.get(key);
    return state ? state.released : false;
  }

  // === Mouse Query Methods ===

  /**
   * Get current mouse position
   */
  public getMousePosition(): Vector2 {
    return { ...this.mousePosition };
  }

  /**
   * Get mouse delta (movement since last frame)
   */
  public getMouseDelta(): Vector2 {
    return { ...this.mouseDelta };
  }

  /**
   * Check if a mouse button is currently down
   * @param button 0 = left, 1 = middle, 2 = right
   */
  public isMouseButtonDown(button: number): boolean {
    return this.mouseButtons[button] || false;
  }

  /**
   * Get mouse wheel delta
   */
  public getMouseWheel(): number {
    return this.mouseWheel;
  }

  // === Action-Based Input (using key bindings) ===

  /**
   * Set key bindings for action-based input
   */
  public setKeyBindings(bindings: KeyBindings): void {
    this.keyBindings = bindings;
  }

  /**
   * Check if an action is currently active (key down)
   */
  public isActionDown(action: InputAction): boolean {
    if (!this.keyBindings) {
      return false;
    }

    const key = this.keyBindings[action];
    return key ? this.isKeyDown(key) : false;
  }

  /**
   * Check if an action was just pressed this frame
   */
  public isActionPressed(action: InputAction): boolean {
    if (!this.keyBindings) {
      return false;
    }

    const key = this.keyBindings[action];
    return key ? this.isKeyPressed(key) : false;
  }

  /**
   * Check if an action was just released this frame
   */
  public isActionReleased(action: InputAction): boolean {
    if (!this.keyBindings) {
      return false;
    }

    const key = this.keyBindings[action];
    return key ? this.isKeyReleased(key) : false;
  }

  // === Save/Load Configuration ===

  /**
   * Save key bindings to localStorage
   */
  public saveKeyBindings(): void {
    if (!this.keyBindings) {
      console.warn('InputManager: No key bindings to save');
      return;
    }

    try {
      const bindingsJson = JSON.stringify(this.keyBindings);
      localStorage.setItem('inputKeyBindings', bindingsJson);
      console.log('InputManager: Key bindings saved');
    } catch (error) {
      console.error('InputManager: Failed to save key bindings', error);
    }
  }

  /**
   * Load key bindings from localStorage
   */
  public loadKeyBindings(): boolean {
    try {
      const bindingsJson = localStorage.getItem('inputKeyBindings');
      if (!bindingsJson) {
        console.log('InputManager: No saved key bindings found');
        return false;
      }

      const bindings = JSON.parse(bindingsJson) as KeyBindings;
      this.keyBindings = bindings;
      console.log('InputManager: Key bindings loaded');
      return true;
    } catch (error) {
      console.error('InputManager: Failed to load key bindings', error);
      return false;
    }
  }

  /**
   * Reset key bindings to default
   */
  public resetKeyBindings(): void {
    // Import default bindings from state/types
    import('@state/types').then(({ DEFAULT_KEY_BINDINGS }) => {
      this.keyBindings = { ...DEFAULT_KEY_BINDINGS };
      this.saveKeyBindings();
      console.log('InputManager: Key bindings reset to default');
    });
  }

  /**
   * Get current key bindings
   */
  public getKeyBindings(): KeyBindings | null {
    return this.keyBindings;
  }

  // === Control Methods ===

  /**
   * Enable input processing
   */
  public enable(): void {
    this.enabled = true;
  }

  /**
   * Disable input processing
   */
  public disable(): void {
    this.enabled = false;
  }

  /**
   * Check if input is enabled
   */
  public isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Clear all input state
   */
  public clear(): void {
    this.keys.clear();
    this.keysToUpdate.clear();
    this.mouseButtons = [false, false, false];
    this.mouseWheel = 0;
    this.mouseDelta = { x: 0, y: 0 };
  }

  /**
   * Clean up event listeners
   */
  public destroy(): void {
    window.removeEventListener('keydown', this.handleKeyDown.bind(this));
    window.removeEventListener('keyup', this.handleKeyUp.bind(this));
    window.removeEventListener('mousemove', this.handleMouseMove.bind(this));
    window.removeEventListener('mousedown', this.handleMouseDown.bind(this));
    window.removeEventListener('mouseup', this.handleMouseUp.bind(this));
    window.removeEventListener('wheel', this.handleMouseWheel.bind(this));
    window.removeEventListener('blur', this.handleWindowBlur.bind(this));

    this.clear();
  }
}

// Singleton instance
let inputManagerInstance: InputManager | null = null;

/**
 * Get the singleton InputManager instance
 */
export function getInputManager(): InputManager {
  if (!inputManagerInstance) {
    inputManagerInstance = new InputManager();
  }
  return inputManagerInstance;
}
